import { useCallback } from 'react'
import { usePaletteTheme } from '../../context/paletteTheme'
import { Bar, Line } from "@visx/shape"
import { localPoint } from "@visx/event"
import { getDate, getValue, bisectDate } from './graphUtils'
import { numberFormatter } from '../../utils/tools'

export default function GraphTooltipArea({
  tooltip,
  margin = { top: 0, left: 0, bottom: 0, right: 0},
  width,
  height,
  xScale,
  yScales,
  layers,
}) {
    const { hideTooltip, tooltipData, showTooltip, tooltipLeft } = tooltip
    const { colors } = usePaletteTheme()
    // tooltip handler - returns tooltip data for use here and in GraphTooltipBubble
    const handleTooltip = useCallback((event) => {
      const { x, y } = localPoint(event) || { x: 0, y: 0 }
      const xDate = xScale.invert(x - margin.left)

      const tooltipData = layers.reduce((records, layer, i) => {
        if (layer?.data?.[0] && layer.display !== 'none') {
          const index = bisectDate(layer.data, xDate)
          const value = getValue(layer.data[index])
          const date = getDate(layer.data[index])
          const top = Array.isArray(yScales) ? yScales[i](value) : yScales(value)
          const left = xScale(date) + margin.left
          const leftClient = (event?.clientX || 0) - x + left
          const topClient = (event?.clientY || 0) - y + top
          const formatNumber = numberFormatter(layer.format)
  
          const record = {
            color: layer.color,
            label: layer.name,
            value: formatNumber(getValue(layer.data[index])),
            i,
            left,
            leftClient,
            topClient,
            date,
            top,
          }
          records.push(record)
        }
        return records
      }, [])

      showTooltip({
        tooltipData,
        tooltipLeft: Math.min(Math.max(x, margin.left), width + margin.left), // x position of the mouse cursor capped at graph width
      })
    }, [showTooltip, xScale, yScales, layers, margin, width])

    const tooltipDots = tooltipData?.map((record, i) => (
        <circle
          key={`circle-${record.label}-${i}`}
          cx={record.left}
          cy={record.top + margin.top}
          r={5}
          fill={record.color}
          stroke={colors.dark1}
          strokeWidth={1}
          pointerEvents="none"
        />
    ))

    return (
      <>
        <Bar
          y={margin.top}
          width={width + margin.left + margin.right}
          height={height}
          fill="transparent"
          onTouchStart={handleTooltip}
          onTouchMove={handleTooltip}
          onMouseMove={handleTooltip}
          onMouseLeave={hideTooltip}
        />
        { tooltipData && (
          <g>
            <Line
              from={{ x: tooltipLeft, y: margin.top }}
              to={{ x: tooltipLeft, y: margin.top + height }}
              stroke="white"
              opacity={0.5}
              strokeWidth={1}
              pointerEvents="none"
              strokeDasharray="3,2"
            /> 
            { tooltipDots }
          </g>
        )}
      </>
    )
}